"use client"

import { useState } from "react"
import { Search, Book } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SIC_CODES, SIC_CATEGORIES } from "@/lib/sic-codes"

const CATEGORIES = SIC_CATEGORIES

interface SICCodeLookupProps {
  onSelect: (sicCode: string) => void
}

export function SICCodeLookup({ onSelect }: SICCodeLookupProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [isOpen, setIsOpen] = useState(false)

  const filteredCodes = SIC_CODES.filter((item) => {
    const q = searchTerm.trim().toLowerCase()
    const matchesSearch =
      !q ||
      item.code.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q)

    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleSelect = (sicCode: string) => {
    const normalized = sicCode.padStart(2, "0").slice(0, 2)
    onSelect(normalized)
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="default" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2">
          <Book className="h-4 w-4 mr-2" />
          SIC Lookup
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-auto">
        <DialogHeader>
          <DialogTitle>SIC Major Group (2-digit)</DialogTitle>
          <DialogDescription>
            Search and select a 2-digit Standard Industrial Classification major group
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by 2-digit code or description…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-auto border rounded-md p-2">
            {filteredCodes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No SIC major groups found</div>
            ) : (
              filteredCodes.map((item) => (
                <button
                  key={item.code}
                  onClick={() => handleSelect(item.code)}
                  className="flex justify-between items-center p-3 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="font-mono text-xs bg-muted px-2 py-1 rounded shrink-0">{item.code}</span>
                    <span className="text-sm text-muted-foreground truncate">{item.description}</span>
                  </div>
                  <div className="text-xs text-muted-foreground bg-muted/70 px-2 py-1 rounded ml-3 shrink-0">{item.category}</div>
                </button>
              ))
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Showing {filteredCodes.length} of {SIC_CODES.length} major groups
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
